import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, ShoppingBasket, Truck, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ProductCard } from './_components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopHome() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/shop/catalog`).then(r => setFeatured(r.data.slice(0, 8))).catch(() => {});
    axios.get(`${API}/shop/catalog/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-background py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mb-4 text-balance">
            Fresh groceries, <span className="text-primary">delivered to your door.</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Shop premium fruits, vegetables, meats and poultry from Bison Food Solutions — same trusted source, now available online.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); if (search.trim()) window.location.href = `/shop/products?q=${encodeURIComponent(search)}`; }}
            className="max-w-md mx-auto flex gap-2"
            data-testid="shop-search-form"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search for tomatoes, chicken, mango..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-12"
                data-testid="shop-hero-search-input"
              />
            </div>
            <Button type="submit" size="lg" data-testid="shop-hero-search-submit">Search</Button>
          </form>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-4 py-10 grid sm:grid-cols-3 gap-4">
        {[
          { icon: ShoppingBasket, title: 'Hand-picked quality', desc: 'Sourced fresh every morning.' },
          { icon: Truck, title: 'Same-day delivery', desc: 'Order before 2 PM for same-day delivery in Kigali.' },
          { icon: ShieldCheck, title: 'Pay your way', desc: 'Mobile Money, card or cash on delivery.' },
        ].map((v) => (
          <div key={v.title} className="p-5 rounded-xl border border-border bg-card">
            <v.icon className="w-6 h-6 text-primary mb-2" />
            <div className="font-semibold mb-1">{v.title}</div>
            <p className="text-sm text-muted-foreground">{v.desc}</p>
          </div>
        ))}
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-6">
          <h2 className="text-lg font-semibold mb-4">Shop by category</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Link key={cat} to={`/shop/products?category=${encodeURIComponent(cat)}`} className="px-4 py-2 rounded-full border border-border hover:bg-primary hover:text-primary-foreground transition text-sm" data-testid={`shop-cat-${cat}`}>
                {cat}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-2xl font-serif font-bold">Featured products</h2>
          <Link to="/shop/products" className="text-sm text-primary hover:underline" data-testid="shop-view-all-link">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-featured-grid">
          {featured.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}
